/**
 * The clock, as a dependency.
 *
 * Sessions end on a twelve-hour ceiling and a thirty-minute idle window, a
 * terminal lease renews before it lapses, and a business day closes at a
 * boundary. None of that can be tested by sleeping — a test that waits on wall
 * time is flaky on CI and proves nothing about the boundary it claims to cover
 * (Constitution II, and `docs/ui-conventions.md` under Testing).
 *
 * So nothing in this app reads the machine's clock. It takes one.
 */

/** Milliseconds since the Unix epoch, the same units `Date.now()` answers in. */
export type Clock = () => number;

/**
 * The single site in this app that reads the real clock, and the reason the
 * rule above is a rule rather than a ban: the composition root has to get the
 * time from somewhere.
 *
 * It is wired in `main.tsx` and nowhere else. Anything that needs the time
 * takes a `Clock` and is handed this one.
 */
export const systemClock: Clock = () => Date.now();

/**
 * What a clock is showing, as the ISO-8601 instant the API's `occurredAt` fields
 * are declared as.
 *
 * A write that records when the caller says it happened needs one of these, and
 * `new Date().toISOString()` at the call site is the exact thing this module
 * exists to keep out of screens. `new Date(ms)` **parses** a number it was handed
 * rather than reading a clock — the same distinction `lib/instant.ts` draws about
 * parsing what the server sent — so the only reader of the machine's clock is
 * still `systemClock` above.
 *
 * The server keeps its own `recordedAt` alongside whatever this produces and
 * treats that one as authoritative for ordering and for the business day
 * (FR-008, FR-009). So a wrong answer here is recorded and believed by nobody,
 * which is the whole reason a caller's clock is allowed on the wire at all.
 */
export const instantOf = (clock: Clock): string => new Date(clock()).toISOString();

/**
 * The instant now, from the real clock.
 *
 * The bound convenience, in the shape `newUuidv7` uses and for the same reason:
 * the injectable form above is what a test pins, and a screen that genuinely
 * means "now" should not have to thread a provider through the tree to say so.
 * Anything whose *behaviour* turns on the value takes a `Clock`.
 */
export const instantNow = (): string => instantOf(systemClock);
